import { analytics } from '@/features/analytics/events'
import { BackToListButton } from '@/features/missions/components/BackToListButton'
import { MissionBadge } from '@/features/missions/components/MissionBadge'
import { MissionOnboarding } from '@/features/missions/components/MissionOnboarding'
import { MissionRewardBadge } from '@/features/missions/components/MissionRewardBadge'
import { useMissionsUrl } from '@/features/missions/context/MissionsUrlProvider'
import { Button, Spinner, Text } from '@/ui-kit'
import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { NECTAR_TYPE_TEXT_MAP, NectarEnrollmentStatus, NectarProject } from '../api/models'
import { isProlificMode } from '../prolific/session'
import { NectarTaskExpired } from './NectarTaskExpired'
import { NectarTaskFlow } from './NectarTaskFlow'
import { ConversationInviteLanding } from './voice-conversation/ConversationInviteLanding'

const STATUS_BADGE_TEXT: Record<NectarEnrollmentStatus, MessageDescriptor> = {
  IN_PROGRESS: msg`In Progress`,
  PENDING_REVIEW: msg`Under review`,
  APPROVED: msg`Completed`,
  REJECTED: msg`Rejected`,
  EXPIRED: msg`Expired`
}

export const NectarTask = ({
  project,
  isFetchingProject,
  isAuthorized = false
}: {
  project?: NectarProject
  isFetchingProject: boolean
  isAuthorized: boolean
}) => {
  const { t } = useLingui()
  const { missionsUrl } = useMissionsUrl()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const hasInviteCode = !!searchParams.get('inviteCode')
  const prolificMode = isProlificMode()

  /* START:Analytics */
  const projectType = project?.type
  const enrollmentStatus = project?.enrollment?.status
  const prevEnrollmentStatusRef = useRef<NectarEnrollmentStatus | undefined>(undefined)
  useEffect(() => {
    if (projectType) analytics.missionOpened(project.id, projectType)
  }, [project?.id, projectType])

  useEffect(() => {
    if (prevEnrollmentStatusRef.current === 'IN_PROGRESS' && enrollmentStatus === 'PENDING_REVIEW' && projectType) {
      analytics.missionCompleted(project.id, projectType)
    }
    prevEnrollmentStatusRef.current = enrollmentStatus
  }, [enrollmentStatus, project?.id, projectType])
  /* END: Analytics */

  if (isFetchingProject) {
    return (
      <div className="flex w-full justify-center py-6">
        <Spinner className="mt-8" />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Text type="h3" className="mb-4">
          <Trans>Project not found</Trans>
        </Text>
        {!prolificMode && (
          <Button onClick={() => navigate(missionsUrl)}>
            <Trans>Back to Missions</Trans>
          </Button>
        )}
      </div>
    )
  }

  const isExpired = enrollmentStatus === 'EXPIRED'
  const isActive = ['APPROVED', 'REJECTED', 'IN_PROGRESS', 'PENDING_REVIEW'].includes(enrollmentStatus ?? '')

  if (!isAuthorized && hasInviteCode && project.type === 'VOICE_CONVERSATION') {
    return <ConversationInviteLanding projectId={project.id} isAuthorized={isAuthorized} />
  }

  return (
    <>
      <div className="max-mobile:gap-4 mb-10 flex flex-col gap-6 py-8">
        {!prolificMode && (
          <div className="absolute top-10 left-6 z-1">
            <BackToListButton href={missionsUrl} />
          </div>
        )}
        {!project.enrolled ? (
          <MissionOnboarding project={project} isAuthorized={isAuthorized} />
        ) : (
          <>
            <div className="flex flex-wrap justify-between gap-1 gap-y-3">
              <div className="max-mobile:mt-12 ml-auto flex gap-1">
                <MissionBadge variant="success">
                  {project.enrollment ? t(STATUS_BADGE_TEXT[project.enrollment!.status]) : t`New`}
                </MissionBadge>
                <MissionBadge variant="default">
                  {NECTAR_TYPE_TEXT_MAP[project.type] ? t(NECTAR_TYPE_TEXT_MAP[project.type]) : project.type}
                </MissionBadge>
                {!!project.rewardConfig?.amount && (
                  <MissionRewardBadge amount={project.totalReward || 0} rewardType={project.rewardConfig?.type} />
                )}
              </div>
            </div>
            {isExpired && <NectarTaskExpired project={project} />}
            {isActive && (
              <>
                <NectarTaskFlow project={project} />
              </>
            )}
          </>
        )}
      </div>
    </>
  )
}
